import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, CheckCircle2, XCircle,
  MinusCircle, ChevronDown, Loader2, Gauge,
} from 'lucide-react';
import { useInspectionStore } from '@/context/inspectionStore';
import { getManualCheckIds, getSamsaraCheckIds, IMAGE_AND_MANUAL_CHECK_IDS } from '@/utils/manualChecks';
import type { CheckStatus, InspectionType } from '@/types';

export function InspectionFormPage() {
  const navigate = useNavigate();
  const inspection = useInspectionStore((s) => s.activeInspection);
  const categoryIdx = useInspectionStore((s) => s.currentCategoryIndex);
  const setCategoryIdx = useInspectionStore((s) => s.setCurrentCategoryIndex);
  const setCheckStatus = useInspectionStore((s) => s.setCheckStatus);
  const setCheckNotes = useInspectionStore((s) => s.setCheckNotes);
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = React.useState(0);

  const inspType = (inspection?.type ?? 'daily') as InspectionType;
  const manualCheckIds = getManualCheckIds(inspType);
  const samsaraCheckIds = getSamsaraCheckIds(inspType);

  React.useEffect(() => {
    if (!headerRef.current) return;
    const observer = new ResizeObserver(entries => {
      setHeaderHeight(entries[0].contentRect.height);
    });
    observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    const main = document.querySelector('main');
    if (main) main.scrollTo({ top: 0, behavior: 'smooth' });
  }, [categoryIdx]);

  React.useEffect(() => {
    if (!inspection) navigate('/');
  }, [inspection, navigate]);

  if (!inspection) return null;

  const manualCategories = inspection.categories
    .map(cat => ({
      ...cat,
      checks: cat.checks.filter(ch =>
        manualCheckIds.has(ch.id) || samsaraCheckIds.has(ch.id)
      ),
    }))
    .filter(cat => cat.checks.length > 0);

  const category = manualCategories[categoryIdx];
  const isLast = categoryIdx === manualCategories.length - 1;
  const isFirst = categoryIdx === 0;

  const manualChecks = manualCategories.flatMap(c => c.checks);
  const totalRequired = manualChecks.filter(ch => ch.required).length;
  const completedRequired = manualChecks.filter(ch => ch.required && ch.status !== null).length;

  const categoryStats = category.checks.reduce(
    (acc, ch) => {
      if (ch.status === 'pass') acc.pass++;
      else if (ch.status === 'fail') acc.fail++;
      else if (ch.status === 'na') acc.na++;
      else acc.pending++;
      return acc;
    },
    { pass: 0, fail: 0, na: 0, pending: 0 }
  );

  return (
    <div className="mx-auto max-w-lg dark:bg-gray-800 min-h-full">
      {/* Sticky header */}
      <div ref={headerRef} className="sticky top-14 z-20 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">
              {inspection.type} — {inspection.vehicleUnitNumber}
            </p>
            <h2 className="text-lg font-bold text-wire3-navy dark:text-wire3-green">{category.name}</h2>
            <p className="text-xs text-gray-400 dark:text-gray-500">Manual & functional checks</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {categoryIdx + 1} / {manualCategories.length}
            </p>
            <p className="text-sm font-semibold text-wire3-navy dark:text-wire3-green">
              {completedRequired}/{totalRequired}
            </p>
          </div>
        </div>

        <div className="mt-2 flex gap-1">
          {manualCategories.map((cat, i) => {
            const done = cat.checks.every((ch) => !ch.required || ch.status !== null);
            const hasFail = cat.checks.some((ch) => ch.status === 'fail');
            return (
              <button
                key={cat.id}
                onClick={() => setCategoryIdx(i)}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  i === categoryIdx ? 'bg-wire3-green' :
                  hasFail ? 'bg-status-fail' :
                  done ? 'bg-wire3-green/40' :
                  'bg-gray-200 dark:bg-gray-700'
                }`}
              />
            );
          })}
        </div>

        <div className="mt-2 flex gap-3 text-xs">
          <span className="text-status-pass">{categoryStats.pass} pass</span>
          <span className="text-status-fail">{categoryStats.fail} fail</span>
          <span className="text-gray-400">{categoryStats.na} N/A</span>
          <span className="text-gray-400">{categoryStats.pending} pending</span>
        </div>
      </div>

      {/* Check items */}
      <div
        className="flex flex-col gap-3 px-4 pb-32"
        style={{ paddingTop: `${headerHeight + 16}px` }}
      >
        {category.checks.map((check) => {
          if (samsaraCheckIds.has(check.id)) {
            return (
              <SamsaraCheckItem
                key={check.id}
                label={check.label}
                required={check.required}
                status={check.status}
                notes={check.notes}
                vehicleId={inspection.vehicleId}
                onStatusChange={(s) => setCheckStatus(category.id, check.id, s)}
                onNotesChange={(n) => setCheckNotes(category.id, check.id, n)}
              />
            );
          }
          return (
            <CheckItem
              key={check.id}
              label={check.label}
              required={check.required}
              status={check.status}
              notes={check.notes}
              isImageAndManual={IMAGE_AND_MANUAL_CHECK_IDS.has(check.id)}
              onStatusChange={(s) => setCheckStatus(category.id, check.id, s)}
              onNotesChange={(n) => setCheckNotes(category.id, check.id, n)}
            />
          );
        })}
      </div>

      {/* Navigation */}
      <div className="fixed bottom-16 left-0 right-0 z-20 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 sm:bottom-0">
        <div className="mx-auto flex max-w-lg gap-3">
          <button
            onClick={() => setCategoryIdx(Math.max(0, categoryIdx - 1))}
            disabled={isFirst}
            className="btn-secondary flex-1 disabled:opacity-40"
          >
            <ArrowLeft className="h-4 w-4" />
            Previous
          </button>
          {isLast ? (
            <button onClick={() => navigate('/inspect/review')} className="btn-primary flex-1">
              Review & Submit
              <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button onClick={() => setCategoryIdx(categoryIdx + 1)} className="btn-primary flex-1">
              Next
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Manual Check Item
// ============================================================

interface CheckItemProps {
  label: string;
  required: boolean;
  status: CheckStatus | null;
  notes: string;
  isImageAndManual?: boolean;
  onStatusChange: (status: CheckStatus) => void;
  onNotesChange: (notes: string) => void;
}

function CheckItem({
  label, required, status, notes, isImageAndManual,
  onStatusChange, onNotesChange,
}: CheckItemProps) {
  const [expanded, setExpanded] = React.useState(false);

  const statusButtons: { value: CheckStatus; label: string; icon: React.ReactNode; className: string }[] = [
    { value: 'pass', label: 'Pass', icon: <CheckCircle2 className="h-5 w-5" />, className: status === 'pass' ? 'check-pass' : 'check-unset' },
    { value: 'fail', label: 'Fail', icon: <XCircle className="h-5 w-5" />, className: status === 'fail' ? 'check-fail' : 'check-unset' },
    { value: 'na', label: 'N/A', icon: <MinusCircle className="h-5 w-5" />, className: status === 'na' ? 'check-na' : 'check-unset' },
  ];

  return (
    <div className={`card dark:bg-gray-750 dark:border-gray-700 transition-shadow ${status === 'fail' ? 'ring-2 ring-status-fail/30' : ''}`}>
      <button onClick={() => setExpanded(!expanded)} className="flex w-full items-start gap-2 text-left">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</p>
          <div className="flex items-center gap-2 mt-0.5">
            {required && (
              <span className="text-[10px] font-semibold uppercase tracking-wider text-status-fail">Required</span>
            )}
            {isImageAndManual && (
              <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-500">
                Functional check
              </span>
            )}
          </div>
        </div>
        <ChevronDown className={`mt-0.5 h-4 w-4 shrink-0 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {isImageAndManual && (
        <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
          Physical damage assessed from photo. Confirm functionality below.
        </p>
      )}

      <div className="mt-3 flex gap-2">
        {statusButtons.map((btn) => (
          <button key={btn.value} onClick={() => onStatusChange(btn.value)} className={btn.className}>
            {btn.icon}
            {btn.label}
          </button>
        ))}
      </div>

      {expanded && (
        <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-3">
          <textarea
            placeholder="Add notes..."
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            rows={2}
            className="input-field text-sm"
          />
        </div>
      )}
    </div>
  );
}

// ============================================================
// Samsara Check Item — shows live tire pressure with manual fallback
// ============================================================

interface SamsaraCheckItemProps {
  label: string;
  required: boolean;
  status: CheckStatus | null;
  notes: string;
  vehicleId: string;
  onStatusChange: (status: CheckStatus) => void;
  onNotesChange: (notes: string) => void;
}

function SamsaraCheckItem({
  label, required, status, notes, vehicleId,
  onStatusChange, onNotesChange,
}: SamsaraCheckItemProps) {
  const [loading, setLoading] = React.useState(true);
  const [tirePressure, setTirePressure] = React.useState<Record<string, number> | null>(null);
  const [samsaraAvailable, setSamsaraAvailable] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);

  // Manual input state — one value per tire
  const TIRE_LABELS = ['Front Left', 'Front Right', 'Rear Left', 'Rear Right'];
  const [manualPressures, setManualPressures] = React.useState<Record<string, string>>({
    'Front Left': '',
    'Front Right': '',
    'Rear Left': '',
    'Rear Right': '',
  });

  const MIN_PSI = 32; // minimum acceptable tire pressure

  React.useEffect(() => {
    fetch(`/api/fleet/tire-pressure/${vehicleId}`)
      .then(r => r.json())
      .then(data => {
        setSamsaraAvailable(data.available);

        if (data.available && data.pressure) {
          const normalized: Record<string, number> = {};

          function extractPressures(obj: any, prefix = '') {
            for (const [key, val] of Object.entries(obj)) {
              if (typeof val === 'number') {
                normalized[prefix ? `${prefix} ${key}` : key] = Math.round(val * 0.145038);
              } else if (typeof val === 'object' && val !== null) {
                if ('value' in val && typeof (val as any).value === 'number') {
                  normalized[prefix ? `${prefix} ${key}` : key] = Math.round((val as any).value * 0.145038);
                } else {
                  extractPressures(val, key);
                }
              }
            }
          }

          extractPressures(data.pressure);
          setTirePressure(normalized);

          const pressures = Object.values(normalized);
          if (pressures.length > 0) {
            onStatusChange(pressures.every(p => p >= MIN_PSI) ? 'pass' : 'fail');
          }
        }
      })
      .catch(() => setSamsaraAvailable(false))
      .finally(() => setLoading(false));
  }, [vehicleId]);

  // Auto-grade manual inputs when all four tires are filled in
  function handleManualInput(tire: string, value: string) {
    const updated = { ...manualPressures, [tire]: value };
    setManualPressures(updated);

    const values = Object.values(updated).map(v => parseFloat(v));
    const allFilled = values.every(v => !isNaN(v) && v > 0);

    if (allFilled) {
      const allGood = values.every(v => v >= MIN_PSI);
      onStatusChange(allGood ? 'pass' : 'fail');
    }
  }

  const statusButtons: { value: CheckStatus; label: string; icon: React.ReactNode; className: string }[] = [
    { value: 'pass', label: 'Pass', icon: <CheckCircle2 className="h-5 w-5" />, className: status === 'pass' ? 'check-pass' : 'check-unset' },
    { value: 'fail', label: 'Fail', icon: <XCircle className="h-5 w-5" />, className: status === 'fail' ? 'check-fail' : 'check-unset' },
    { value: 'na', label: 'N/A', icon: <MinusCircle className="h-5 w-5" />, className: status === 'na' ? 'check-na' : 'check-unset' },
  ];

  return (
    <div className={`card dark:bg-gray-750 dark:border-gray-700 transition-shadow ${status === 'fail' ? 'ring-2 ring-status-fail/30' : ''}`}>
      <button onClick={() => setExpanded(!expanded)} className="flex w-full items-start gap-2 text-left">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</p>
          <div className="flex items-center gap-2 mt-0.5">
            {required && (
              <span className="text-[10px] font-semibold uppercase tracking-wider text-status-fail">Required</span>
            )}
            <span className="text-[10px] font-semibold uppercase tracking-wider text-wire3-green">
              {loading ? 'Loading...' : samsaraAvailable ? 'Samsara data' : 'Manual entry'}
            </span>
          </div>
        </div>
        <ChevronDown className={`mt-0.5 h-4 w-4 shrink-0 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {loading ? (
        <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Fetching tire pressure from Samsara...
        </div>

      ) : samsaraAvailable && tirePressure ? (
        // ── Samsara live data ──────────────────────────────
        <div className="mt-3 rounded-lg bg-wire3-navy/5 dark:bg-gray-700 p-3">
          <p className="text-xs font-semibold text-wire3-navy dark:text-wire3-green mb-2 flex items-center gap-1">
            <Gauge className="h-3.5 w-3.5" />
            Live Tire Pressure (PSI)
          </p>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(tirePressure).map(([tire, psi]) => (
              <div
                key={tire}
                className={`rounded px-2 py-1 text-xs ${
                  psi >= MIN_PSI
                    ? 'bg-status-pass/10 text-status-pass'
                    : 'bg-status-fail/10 text-status-fail'
                }`}
              >
                <span className="capitalize">{tire.replace(/([A-Z])/g, ' $1').trim()}</span>
                <span className="ml-1 font-bold">{psi} PSI</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[10px] text-gray-400">
            Auto-graded from Samsara. Minimum {MIN_PSI} PSI. Override below if needed.
          </p>
        </div>

      ) : (
        // ── Manual tire pressure input ─────────────────────
        <div className="mt-3">
          <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2 flex items-center gap-1">
            <Gauge className="h-3.5 w-3.5" />
            Enter Tire Pressure (PSI)
          </p>
          <div className="grid grid-cols-2 gap-2">
            {TIRE_LABELS.map(tire => {
              const val = parseFloat(manualPressures[tire]);
              const filled = !isNaN(val) && val > 0;
              const passing = filled && val >= MIN_PSI;
              return (
                <div key={tire} className="flex flex-col gap-1">
                  <label className="text-[10px] text-gray-400 dark:text-gray-500">{tire}</label>
                  <div className="relative">
                    <input
                      type="number"
                      inputMode="decimal"
                      placeholder="e.g. 35"
                      value={manualPressures[tire]}
                      onChange={e => handleManualInput(tire, e.target.value)}
                      className={`w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors
                        dark:bg-gray-700 dark:text-gray-100 placeholder:text-gray-400
                        ${filled
                          ? passing
                            ? 'border-status-pass focus:ring-status-pass/20'
                            : 'border-status-fail focus:ring-status-fail/20'
                          : 'border-gray-300 dark:border-gray-600 focus:border-wire3-green focus:ring-wire3-green/20'
                        } focus:ring-2`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">PSI</span>
                  </div>
                  {filled && (
                    <span className={`text-[10px] font-semibold ${passing ? 'text-status-pass' : 'text-status-fail'}`}>
                      {passing ? '✓ OK' : `✗ Low (min ${MIN_PSI} PSI)`}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-2 text-[10px] text-gray-400 dark:text-gray-500">
            Auto-graded when all four values are entered. Minimum {MIN_PSI} PSI.
          </p>
        </div>
      )}

      {/* Manual override buttons — always shown */}
      <div className="mt-3 flex gap-2">
        {statusButtons.map((btn) => (
          <button key={btn.value} onClick={() => onStatusChange(btn.value)} className={btn.className}>
            {btn.icon}
            {btn.label}
          </button>
        ))}
      </div>

      {expanded && (
        <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-3">
          <textarea
            placeholder="Add notes about tire pressure..."
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            rows={2}
            className="input-field text-sm"
          />
        </div>
      )}
    </div>
  );
}